import React, { memo } from "react";
import {
  Stack,
  Typography,
  CardContent,
  Card,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Icon,
  TableContainer,
} from "investira.react.components";
import { validators } from "investira.sdk";

const AtivoConsistencia = memo((props) => {
  const { pessoa, ativo, cotacao } = props;

  function analisarRetornos(pDados) {
    if (validators.isNull(pDados)) {
      return {};
    }

    const obterChaveMes = (pData) => pData.substring(0, 7);

    // Agrupar dados por mês
    const dadosPorMes = pDados.reduce((acc, item) => {
      const chaveMes = obterChaveMes(item.data);
      if (!acc[chaveMes]) {
        acc[chaveMes] = item;
      } else if (new Date(item.data) > new Date(acc[chaveMes].data)) {
        // Manter o último dia do mês
        acc[chaveMes] = item;
      }
      return acc;
    }, {});

    let xMesesPositivos = 0;
    let xMesesNegativos = 0;
    let xMaiorRetorno = -Infinity;
    let xMenorRetorno = Infinity;
    let xSomaRetornosPositivos = 0;
    let xSomaRetornosNegativos = 0;

    // Ordena os dados por data para garantir a sequência correta
    const meses = Object.values(dadosPorMes).sort(
      (a, b) => new Date(a.data) - new Date(b.data)
    );

    for (let i = 1; i < meses.length; i++) {
      let xRetornoMensal =
        meses[i].cotacao_ajustada / meses[i - 1].cotacao_ajustada - 1;

      xRetornoMensal =
        isNaN(xRetornoMensal) || xRetornoMensal === Infinity
          ? 0
          : xRetornoMensal;

      if (xRetornoMensal > 0) {
        xMesesPositivos++;
        xSomaRetornosPositivos += xRetornoMensal;
      } else if (xRetornoMensal < 0) {
        xMesesNegativos++;
        xSomaRetornosNegativos += xRetornoMensal;
      }

      xMaiorRetorno = Math.max(xMaiorRetorno, xRetornoMensal);
      xMenorRetorno = Math.min(xMenorRetorno, xRetornoMensal);
    }

    const xTotalMeses = xMesesPositivos + xMesesNegativos;
    const xPercentualMesesPositivos = (xMesesPositivos / xTotalMeses) * 100;
    const xPercentualMesesNegativos = (xMesesNegativos / xTotalMeses) * 100;

    const xRetornoMedioPositivo =
      xMesesPositivos > 0 ? xSomaRetornosPositivos / xMesesPositivos : 0;
    const xRetornoMedioNegativo =
      xMesesNegativos > 0 ? xSomaRetornosNegativos / xMesesNegativos : 0;

    return {
      mesesPositivos: xMesesPositivos,
      mesesNegativos: xMesesNegativos,
      maiorRetorno: (xMaiorRetorno * 100).toFixed(2),
      menorRetorno: (xMenorRetorno * 100).toFixed(2),
      percentualMesesPositivos: !isNaN(xPercentualMesesPositivos)
        ? xPercentualMesesPositivos.toFixed(2) + "%"
        : 0 + "%",
      percentualMesesNegativos: !isNaN(xPercentualMesesNegativos)
        ? xPercentualMesesNegativos.toFixed(2) + "%"
        : 0 + "%",
      retornoMedioPositivo: isFinite(xRetornoMedioPositivo)
        ? (xRetornoMedioPositivo * 100).toFixed(2) + "%"
        : 0,
      retornoMedioNegativo: isFinite(xRetornoMedioNegativo)
        ? (xRetornoMedioNegativo * 100).toFixed(2) + "%"
        : 0,
    };
  }

  if (validators.isEmpty(cotacao.historico.full)) {
    return null;
  }

  const xResultado = analisarRetornos(cotacao.historico.full);

  return (
    <Stack flexGrow={1}>
      <Card>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Stack spacing={2} sx={{ py: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                variant="subtitle1"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
              >
                Consistência
              </Typography>
              <Stack alignItems="center" justifyContent="center">
                <Tooltip
                  title={
                    "Meses Positivos: Quantos meses o ativo se valorizou. Meses Negativos: Quantos meses o se desvalorizou. Maior Retorno: Qual o maior retorno que o ativo deu em um mês. Menor Retorno: Qual o menor retorno que o ativo deu em um mês."
                  }
                >
                  <Box sx={{ width: "16px", height: "16px" }}>
                    <Icon iconName="information" size={16} color="primary" />
                  </Box>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack direction="column" sx={{ gap: 1 }}>
              <TableContainer
                id="table-container"
                sx={{
                  overflow: "auto",
                }}
              >
                <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      ></TableCell>
                      <TableCell align="right">Meses Positivos</TableCell>
                      <TableCell align="right">Meses Negativos</TableCell>
                      <TableCell align="right">Maior Retorno</TableCell>
                      <TableCell align="right">Menor Retorno</TableCell>
                      {/* <TableCell align="right">Acima</TableCell>
                    <TableCell align="right">Abaixo</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      >
                        <b>
                          {ativo.ticker || ativo.ativo_seuid || ativo.ativo}
                        </b>
                      </TableCell>
                      <TableCell align="right">
                        <Stack>
                          <Typography variant="caption">
                            {xResultado.mesesPositivos}
                          </Typography>
                          <Typography variant="caption">
                            ({xResultado.percentualMesesPositivos})
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack>
                          <Typography variant="caption">
                            {xResultado.mesesNegativos}
                          </Typography>
                          <Typography variant="caption">
                            ({xResultado.percentualMesesNegativos})
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack>
                          <Typography
                            variant="caption"
                            color={xResultado.maiorRetorno < 0 && "error.light"}
                          >
                            {xResultado.maiorRetorno}%
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack>
                          <Typography
                            variant="caption"
                            color={xResultado.menorRetorno < 0 && "error.light"}
                          >
                            {xResultado.menorRetorno}%
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoConsistencia;
